import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';

import CustomAppBar from '../components/CustomAppBar';
import CircularTimer from '../components/timer/CircularTimer';
import TimerControls from '../components/timer/TimerControls';
import TimerSettingsPanel from '../components/timer/TimerSettingsPanel';
import TimerStats from '../components/timer/TimerStats';
import TaskPanel from '../components/timer/TaskPanel';
import AnalyticsPanel from '../components/timer/AnalyticsPanel';
import useTimerWebSocket from '../hooks/useTimerWebSocket';
import useServiceWorker from '../hooks/useServiceWorker';
import timerService from '../services/timerService';

// Bell chime using the Web Audio API — works on all desktop browsers
const playBellChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const playTone = (freq, startTime, duration, gain = 0.4) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(gain, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    const t = ctx.currentTime;
    // C5 → E5 → G5 → C6 — a pleasant ascending bell chord
    playTone(523.25, t,        1.5, 0.3);
    playTone(659.25, t + 0.15, 1.5, 0.25);
    playTone(783.99, t + 0.30, 1.5, 0.2);
    playTone(1046.5, t + 0.45, 2.0, 0.15);
  } catch (e) {
    console.warn('Audio playback failed:', e);
  }
};

// ──────────────────────────────────────────────
// Pomodoro indicator dots
// ──────────────────────────────────────────────
const PomodoroIndicator = ({ count, total }) => (
  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1 }}>
    {Array.from({ length: total }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: i < count ? '#e53935' : 'rgba(255,255,255,0.15)',
          transition: 'background-color 0.4s ease',
          boxShadow: i < count ? '0 0 8px rgba(229,57,53,0.6)' : 'none',
        }}
      />
    ))}
  </Box>
);

// ──────────────────────────────────────────────
// Mode tab pills
// ──────────────────────────────────────────────
const MODE_LABELS = {
  FOCUS: 'Focus',
  SHORT_BREAK: 'Short Break',
  LONG_BREAK: 'Long Break',
};

const ModeTabs = ({ mode }) => (
  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
    {Object.entries(MODE_LABELS).map(([key, label]) => (
      <Chip
        key={key}
        label={label}
        size="small"
        sx={{
          backgroundColor: mode === key ? 'rgba(255,255,255,0.15)' : 'transparent',
          color: mode === key ? 'white' : 'rgba(255,255,255,0.35)',
          border: mode === key ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent',
          fontWeight: mode === key ? 600 : 400,
          fontSize: '0.75rem',
          transition: 'all 0.3s ease',
          cursor: 'default',
        }}
      />
    ))}
  </Box>
);

// ──────────────────────────────────────────────
// Background gradient per mode
// ──────────────────────────────────────────────
const MODE_GRADIENTS = {
  FOCUS: 'linear-gradient(135deg, #1a0a0a 0%, #2d1515 50%, #1a0505 100%)',
  SHORT_BREAK: 'linear-gradient(135deg, #071a0a 0%, #0f2d15 50%, #051a09 100%)',
  LONG_BREAK: 'linear-gradient(135deg, #050f1a 0%, #0a1f2d 50%, #040d1a 100%)',
};

// ──────────────────────────────────────────────
// Compute remaining seconds from server state
// ──────────────────────────────────────────────
const computeRemaining = (timerState) => {
  if (!timerState) return 0;
  const { status, durationSeconds, startedAtEpochMs, elapsedAtPause } = timerState;
  if (status === 'IDLE') return durationSeconds;
  if (status === 'PAUSED') return Math.max(0, durationSeconds - elapsedAtPause);
  if (status === 'RUNNING' && startedAtEpochMs) {
    const elapsedSinceStart = (Date.now() - startedAtEpochMs) / 1000;
    return Math.max(0, durationSeconds - elapsedAtPause - elapsedSinceStart);
  }
  return durationSeconds;
};

// ──────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────
const TimerPage = () => {
  const [timerState, setTimerState] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState('timer'); // 'timer' | 'analytics'
  const [showIosInstallBanner, setShowIosInstallBanner] = useState(false);
  // Incremented each time a focus session completes — triggers TaskPanel to refresh task counts
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);
  const intervalRef = useRef(null);
  const completedRef = useRef(false); // prevent double-completion
  const prevPomodoroCountRef = useRef(null); // track last known count to detect increments

  // Detect iOS Safari running outside of standalone PWA mode
  useEffect(() => {
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandaloneMode = window.navigator.standalone === true;
    if (isIos && !isInStandaloneMode) {
      setShowIosInstallBanner(true);
    }
  }, []);

  // ── Load initial state + stats via REST ──
  useEffect(() => {
    const init = async () => {
      try {
        const [state, statsData] = await Promise.all([
          timerService.getState(),
          timerService.getStats(),
        ]);
        setTimerState(state);
        setRemaining(Math.round(computeRemaining(state)));
        setStats(statsData);
      } catch (e) {
        console.error('Failed to load timer state:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ── Handle incoming WebSocket state updates ──
  const handleStateUpdate = useCallback((newState) => {
    setTimerState((prev) => {
      // Detect a new pomodoro being credited: pomodoroCount went up OR we just transitioned
      // from RUNNING/FOCUS back to IDLE — meaning a focus session completed.
      const prevCount = prevPomodoroCountRef.current;
      const newCount = newState.pomodoroCount ?? 0;
      if (prevCount !== null && newCount !== prevCount) {
        // A pomodoro was completed — refresh task list to show updated counts
        setTaskRefreshKey((k) => k + 1);
      }
      prevPomodoroCountRef.current = newCount;
      return newState;
    });
    setRemaining(Math.round(computeRemaining(newState)));
    completedRef.current = false;
    // Refresh stats after a completion
    if (newState.status === 'IDLE' && newState.pomodoroCount > 0) {
      timerService.getStats().then(setStats).catch(() => {});
    }
  }, []);

  // Exposed so TaskPanel can feed back activate/deactivate state changes
  const handleTaskStateUpdate = useCallback((newState) => {
    setTimerState(newState);
  }, []);

  const { connected, sendCommand } = useTimerWebSocket(handleStateUpdate);
  const {
    scheduleTimerNotification,
    cancelTimerNotification,
    showNotificationViaSW,
    sendTestNotification,
    isPushSubscribed,
    subscribeToWebPush,
    unsubscribeFromWebPush,
    sendTestPushAlert,
  } = useServiceWorker();

  const [notifPermission, setNotifPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );

  const handleRequestPermission = async () => {
    if (!('Notification' in window)) {
      setSnackbar({ open: true, message: 'Notifications are not supported in this browser', severity: 'warning' });
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setNotifPermission(perm);
      if (perm === 'granted') {
        showNotificationViaSW('🎉 Notifications Enabled!', 'You will receive alerts when timer sessions complete.', 'TEST');
        subscribeToWebPush();
        setSnackbar({ open: true, message: 'Notifications & Web Push enabled successfully! ✓', severity: 'success' });
      } else if (perm === 'denied') {
        setSnackbar({ open: true, message: 'Notification permission denied. Check iOS/browser settings.', severity: 'error' });
      }
    } catch (e) {
      console.error('Permission request failed:', e);
      setSnackbar({ open: true, message: 'Failed to request notification permission', severity: 'error' });
    }
  };

  // ── Local tick ──
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (timerState?.status === 'RUNNING') {
      intervalRef.current = setInterval(() => {
        setRemaining((prev) => {
          const next = prev - 1;
          if (next <= 0 && !completedRef.current) {
            completedRef.current = true;
            // Fire sound + notification + send COMPLETE
            playBellChime();
            const modeLabel = MODE_LABELS[timerState.mode] || timerState.mode;
            showNotificationViaSW(`${modeLabel} Complete! 🍅`, 'Time to switch — well done!', timerState.mode);
            // Small delay so server gets the command slightly after client catches 0
            setTimeout(() => sendCommand('COMPLETE'), 200);
          }
          return Math.max(0, next);
        });
      }, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [timerState?.status, timerState?.mode, sendCommand, showNotificationViaSW]); // eslint-disable-line

  // Resync timer when returning from background (iOS PWA freezes timers when backgrounded)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        try {
          const state = await timerService.getState();
          setTimerState(state);
          const rem = Math.round(computeRemaining(state));
          setRemaining(rem);
          if (state.status === 'RUNNING' && rem <= 0 && !completedRef.current) {
            completedRef.current = true;
            playBellChime();
            const modeLabel = MODE_LABELS[state.mode] || state.mode;
            showNotificationViaSW(`${modeLabel} Complete! 🍅`, 'Time to switch — well done!', state.mode);
            setTimeout(() => sendCommand('COMPLETE'), 200);
          }
        } catch (e) {
          console.warn('Failed to resync timer on visibility change:', e);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [sendCommand, showNotificationViaSW]);

  // ── Commands — also sync notification scheduling with the Service Worker ──
  const handleCommand = useCallback((cmd) => {
    if ((cmd === 'START' || cmd === 'RESUME') && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => setNotifPermission(perm)).catch(() => {});
    }

    sendCommand(cmd);

    if (cmd === 'START' || cmd === 'RESUME') {
      // Calculate when this session ends and schedule a SW notification
      const duration = timerState?.durationSeconds || 1500;
      const alreadyElapsed = timerState?.elapsedAtPause || 0;
      const remainingMs = (duration - alreadyElapsed) * 1000;
      const endTimeMs = Date.now() + remainingMs;
      scheduleTimerNotification(endTimeMs, timerState?.mode || 'FOCUS');
    }

    if (cmd === 'PAUSE' || cmd === 'SKIP' || cmd === 'RESET' || cmd === 'COMPLETE') {
      cancelTimerNotification();
    }
  }, [sendCommand, timerState, scheduleTimerNotification, cancelTimerNotification]);

  // ── Settings save ──
  const handleSaveSettings = async (newSettings) => {
    try {
      await timerService.updateSettings(newSettings);
      // Refresh state so new duration is reflected
      const state = await timerService.getState();
      setTimerState(state);
      setRemaining(Math.round(computeRemaining(state)));
      setSnackbar({ open: true, message: 'Settings saved ✓', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#0d0d0d' }}>
        <CircularProgress sx={{ color: 'rgba(255,255,255,0.5)' }} />
      </Box>
    );
  }

  const mode = timerState?.mode || 'FOCUS';
  const status = timerState?.status || 'IDLE';
  const pomodoroCount = timerState?.pomodoroCount || 0;
  const longBreakAfter = timerState?.settings?.longBreakAfter || 4;
  const indicatorCount = pomodoroCount % longBreakAfter;

  return (
    <CustomAppBar
      sx={{
        background: MODE_GRADIENTS[mode],
        transition: 'background 0.8s ease',
      }}
      appBarSx={{
        bgcolor: 'transparent',
        backgroundImage: 'none',
      }}
      containerSx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Connection status badge */}
      <Box sx={{ position: 'fixed', top: 'calc(72px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))', zIndex: 100 }}>
        <Tooltip title={connected ? 'Live sync active' : 'Reconnecting...'}>
          <Chip
            icon={connected ? <WifiRoundedIcon sx={{ fontSize: 14 }} /> : <WifiOffRoundedIcon sx={{ fontSize: 14 }} />}
            label={connected ? 'Live' : 'Offline'}
            size="small"
            sx={{
              backgroundColor: connected ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
              color: connected ? '#4ade80' : '#ef4444',
              border: `1px solid ${connected ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.3)'}`,
              fontSize: '0.7rem',
              fontWeight: 600,
            }}
          />
        </Tooltip>
      </Box>

      {/* ── Tab switcher ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '24px',
          p: '4px',
          mb: 1,
          mt: 0.5,
        }}
      >
        {[{ key: 'timer', label: '⏱ Timer' }, { key: 'analytics', label: '📊 Analytics' }].map(({ key, label }) => (
          <Box
            key={key}
            onClick={() => setActiveTab(key)}
            sx={{
              px: 2.5,
              py: 0.7,
              borderRadius: '20px',
              fontSize: '0.78rem',
              fontWeight: activeTab === key ? 700 : 400,
              cursor: 'pointer',
              background: activeTab === key ? 'rgba(255,255,255,0.12)' : 'transparent',
              color: activeTab === key ? 'white' : 'rgba(255,255,255,0.4)',
              border: activeTab === key ? '1px solid rgba(255,255,255,0.18)' : '1px solid transparent',
              transition: 'all 0.25s ease',
              userSelect: 'none',
              '&:hover': { color: 'rgba(255,255,255,0.75)' },
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {/* ── Analytics tab ── */}
      {activeTab === 'analytics' && (
        <Box sx={{ width: '100%', px: { xs: 1, sm: 2 } }}>
          <AnalyticsPanel />
        </Box>
      )}

      {/* ── Timer tab ── */}
      {activeTab === 'timer' && (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Mode tabs */}
        <ModeTabs mode={mode} />

        {/* Circular timer */}
        <Box sx={{ my: 1 }}>
          <CircularTimer
            remainingSeconds={remaining}
            totalSeconds={timerState?.durationSeconds || 1500}
            mode={mode}
            status={status}
          />
        </Box>

        {/* Active task label */}
        {timerState?.activeTaskName && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              background: 'rgba(229,57,53,0.12)',
              border: '1px solid rgba(229,57,53,0.25)',
              borderRadius: '20px',
              px: 1.5,
              py: 0.35,
              mt: -0.5,
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#e53935', boxShadow: '0 0 5px rgba(229,57,53,0.7)' }} />
            <Typography sx={{ color: '#e57373', fontSize: '0.72rem', fontWeight: 600 }}>
              {timerState.activeTaskName}
            </Typography>
          </Box>
        )}

        {/* Pomodoro dots */}
        <Box>
          <PomodoroIndicator count={indicatorCount} total={longBreakAfter} />
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', textAlign: 'center', mt: 0.5, fontSize: '0.7rem' }}
          >
            {indicatorCount} / {longBreakAfter} until long break · {pomodoroCount} total
          </Typography>
        </Box>

        {/* Controls */}
        <TimerControls
          status={status}
          onStart={() => handleCommand('START')}
          onPause={() => handleCommand('PAUSE')}
          onResume={() => handleCommand('RESUME')}
          onSkip={() => handleCommand('SKIP')}
          onReset={() => handleCommand('RESET')}
        />

        {/* Task panel */}
        <TaskPanel
          activeTaskId={timerState?.activeTaskId ?? null}
          onStateUpdate={handleTaskStateUpdate}
          refreshKey={taskRefreshKey}
        />

        {/* Stats */}
        <TimerStats stats={stats} />

        {/* Settings */}
        <TimerSettingsPanel
          settings={timerState?.settings}
          onSave={handleSaveSettings}
          hasTaskOverride={!!(timerState?.activeTaskId)}
          isPushSubscribed={isPushSubscribed}
          onSubscribePush={async () => {
            const ok = await subscribeToWebPush();
            if (ok) setSnackbar({ open: true, message: 'Web Push alerts enabled! ✓', severity: 'success' });
          }}
          onUnsubscribePush={async () => {
            await unsubscribeFromWebPush();
            setSnackbar({ open: true, message: 'Web Push disabled', severity: 'info' });
          }}
          onTestPush={async () => {
            await sendTestPushAlert();
            setSnackbar({ open: true, message: 'Test background alert sent from server! ✓', severity: 'success' });
          }}
        />

        {/* Notification Permission Card (Required for iOS PWA user gesture) */}
        {notifPermission === 'default' && (
          <Box
            sx={{
              mt: 2,
              maxWidth: 340,
              width: '100%',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              borderRadius: '16px',
              backdropFilter: 'blur(12px)',
              p: 2,
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            }}
          >
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>
              🔔 Enable Timer Notifications
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem', mb: 1.5, lineHeight: 1.4 }}>
              Tap below to allow alerts when your focus sessions or breaks complete (required on iPhone PWA).
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleRequestPermission}
              sx={{
                background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                color: '#111',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '20px',
                px: 3,
                py: 0.6,
                boxShadow: '0 2px 10px rgba(74, 222, 128, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                },
              }}
            >
              Enable Notifications
            </Button>
          </Box>
        )}

        {notifPermission === 'granted' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip
              label="🔔 Notifications Active · Tap to Test"
              size="small"
              onClick={() => {
                showNotificationViaSW('🔔 Test Alert', 'iPhone & PWA notifications are working perfectly!', 'TEST');
                sendTestNotification();
                if (isPushSubscribed) sendTestPushAlert();
                setSnackbar({ open: true, message: 'Test notification sent! ✓', severity: 'success' });
              }}
              sx={{
                backgroundColor: 'rgba(74,222,128,0.12)',
                color: '#4ade80',
                border: '1px solid rgba(74,222,128,0.3)',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { backgroundColor: 'rgba(74,222,128,0.22)', transform: 'scale(1.03)' },
              }}
            />
          </Box>
        )}

        {notifPermission === 'denied' && (
          <Typography
            variant="caption"
            sx={{ color: '#ef4444', textAlign: 'center', mt: 1, fontSize: '0.7rem', maxWidth: 300, background: 'rgba(239,68,68,0.1)', p: 1, borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)' }}
          >
            🔕 Notification permission is blocked. Please allow notifications for this site in iOS/browser Settings.
          </Typography>
        )}

        {/* iOS install prompt — shown only on iOS Safari, not when already a PWA */}
        {showIosInstallBanner && (
          <Box
            sx={{
              mt: 2,
              maxWidth: 340,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '14px',
              backdropFilter: 'blur(12px)',
              p: 2,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Typography sx={{ fontSize: '1.4rem', lineHeight: 1, mt: '2px' }}>📲</Typography>
              <Box>
                <Typography
                  sx={{ color: 'white', fontWeight: 600, fontSize: '0.82rem', mb: 0.5 }}
                >
                  Get notifications on iPhone
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem', lineHeight: 1.5 }}>
                  Tap{' '}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                    Share ⬆️
                  </Box>{' '}
                  in Safari, then{' '}
                  <Box component="span" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                    "Add to Home Screen"
                  </Box>{' '}
                  to enable timer notifications when the app is in the background.
                </Typography>
              </Box>
            </Box>
            <Box
              onClick={() => setShowIosInstallBanner(false)}
              sx={{
                position: 'absolute', top: 8, right: 10,
                cursor: 'pointer', color: 'rgba(255,255,255,0.3)',
                fontSize: '1rem', lineHeight: 1,
                '&:hover': { color: 'rgba(255,255,255,0.6)' },
              }}
            >
              ✕
            </Box>
          </Box>
        )}
      </Box>
      )} {/* end Timer tab */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </CustomAppBar>
  );
};

export default TimerPage;
